/**
 * Rechnet die gemeldete Lage in eine raeumliche Darstellung um (W12).
 *
 * Die Firmware sendet mit `telemetry.send_orientation()` vier Werte:
 * `q_w`, `q_x`, `q_y`, `q_z`. Das ist ein Quaternion - eine Drehung im Raum in
 * vier Zahlen, ohne die Mehrdeutigkeiten einzelner Winkel.
 *
 * Warum das Quaternion und nicht die Matrix uebertragen wird
 * ----------------------------------------------------------
 * Beides beschreibt dieselbe Drehung; die Matrix braucht neun Zahlen statt
 * vier. Sie hier auszurechnen kostet nichts, sie zu uebertragen kostet
 * Uebertragungszeit auf einer Leitung, die sich mit Konsole und Uploadprotokoll
 * teilt. Deshalb kommt das Quaternion, und `rotationMatrix` macht daraus die
 * Matrix.
 *
 * Warum keine 3D-Bibliothek
 * -------------------------
 * Gezeichnet wird ein Koerper aus zwoelf Flaechen. Die Rechnung dafuer ist eine
 * Matrixmultiplikation und eine Projektion, beides hier nachlesbar und
 * pruefbar. Eine Bibliothek waere mehr Abhaengigkeit als Gewinn - und die
 * Entscheidungen, die man pruefen will, lagen dann in ihr.
 *
 * Getrennt von der Darstellung, damit genau diese Entscheidungen pruefbar
 * bleiben: Achsenkonvention, Blickrichtung und die Reihenfolge, in der
 * Flaechen gezeichnet werden.
 */

import type { Channel, TelemetrySeries } from "./series";

/** Namen der vier Kanaele. Muessen zu robot/_telemetry.py passen. */
export const ORIENTATION_CHANNELS = ["q_w", "q_x", "q_y", "q_z"] as const;

export interface Quaternion {
  w: number;
  x: number;
  y: number;
  z: number;
}

export type Vec3 = readonly [number, number, number];
export type Matrix3 = readonly [Vec3, Vec3, Vec3];

/** Steht in den Reihen eine Lage? */
export function hasOrientation(channels: Channel[]): boolean {
  const vorhanden = new Set(channels.map((channel) => channel.name));
  return ORIENTATION_CHANNELS.every((name) => vorhanden.has(name));
}

/**
 * Die jüngste vollstaendige Lage aus den Reihen.
 *
 * Rueckwaerts gesucht und nur vollstaendige Saetze genommen: die vier Werte
 * kommen in einer Zeile, aber andere Kanaele koennen dazwischen liegen und
 * fuellen die Lage dann mit Luecken auf. Ein halber Satz waere keine Drehung.
 *
 * Der Betrag wird auf 1 gebracht. Die Firmware sendet vier einzelne Felder aus
 * dem gemeinsamen Zustand; ein Leser auf dem anderen Kern kann zwei
 * aufeinanderfolgende Staende mischen, und dann liegt der Betrag knapp neben 1.
 */
export function latestQuaternion(
  series: TelemetrySeries
): Quaternion | undefined {
  const reihen = ORIENTATION_CHANNELS.map((name) => series.channel(name));
  if (reihen.some((reihe) => reihe === undefined)) {
    return undefined;
  }
  const werte = reihen as Channel[];
  const laenge = werte[0].values.length;
  for (let zeile = laenge - 1; zeile >= 0; zeile -= 1) {
    const satz = werte.map((reihe) => reihe.values[zeile]);
    if (satz.some((wert) => wert === null || wert === undefined)) {
      continue;
    }
    const [w, x, y, z] = satz as number[];
    const betrag = Math.sqrt(w * w + x * x + y * y + z * z);
    if (!(betrag > 0)) {
      continue;
    }
    return { w: w / betrag, x: x / betrag, y: y / betrag, z: z / betrag };
  }
  return undefined;
}

/**
 * Rotationsmatrix zum Quaternion.
 *
 * Sie dreht vom Fahrzeug- ins Erdsystem: `M * v_fahrzeug = v_erde`. Dieselbe
 * Festlegung wie in `robot/_orientation.py`, damit beide Seiten dieselbe
 * Drehung meinen.
 */
export function rotationMatrix(q: Quaternion): Matrix3 {
  const { w, x, y, z } = q;
  const ww = w * w;
  const xx = x * x;
  const yy = y * y;
  const zz = z * z;
  return [
    [ww + xx - yy - zz, 2 * (x * y - w * z), 2 * (x * z + w * y)],
    [2 * (x * y + w * z), ww - xx + yy - zz, 2 * (y * z - w * x)],
    [2 * (x * z - w * y), 2 * (y * z + w * x), ww - xx - yy + zz],
  ];
}

/** Dreht einen Punkt aus dem Fahrzeug- ins Erdsystem. */
export function rotate(m: Matrix3, p: Vec3): Vec3 {
  return [
    m[0][0] * p[0] + m[0][1] * p[1] + m[0][2] * p[2],
    m[1][0] * p[0] + m[1][1] * p[1] + m[1][2] * p[2],
    m[2][0] * p[0] + m[2][1] * p[1] + m[2][2] * p[2],
  ];
}

/**
 * Blickrichtung.
 *
 * Schraeg von oben, damit alle drei Achsen sichtbar bleiben - genau von einer
 * Seite waere eine Achse ein Punkt und die Drehung um sie unsichtbar.
 *
 * 142 Grad und nicht 38: der Roboter faehrt in Richtung **-x**, und in der
 * Ruhelage soll man ihm ins Gesicht sehen und nicht auf den Ruecken.
 */
export const VIEW_AZIMUTH_DEG = 142;
export const VIEW_ELEVATION_DEG = 22;

export interface ViewBasis {
  right: Vec3;
  up: Vec3;
  depth: Vec3;
}

/**
 * Baut die drei Blickachsen.
 *
 * `depth` zeigt von der Szene zur Kamera: ein groesserer Wert heisst naeher am
 * Betrachter. `right` liegt waagerecht, damit die Senkrechte der Welt auch auf
 * dem Bild senkrecht bleibt.
 */
export function viewBasis(
  azimuthDeg: number = VIEW_AZIMUTH_DEG,
  elevationDeg: number = VIEW_ELEVATION_DEG
): ViewBasis {
  const az = (azimuthDeg * Math.PI) / 180;
  const el = (elevationDeg * Math.PI) / 180;
  const depth: Vec3 = [
    Math.cos(el) * Math.cos(az),
    Math.cos(el) * Math.sin(az),
    Math.sin(el),
  ];
  const right: Vec3 = [-Math.sin(az), Math.cos(az), 0];
  // up = depth x right, damit die drei ein Rechtssystem bilden.
  const up: Vec3 = [
    depth[1] * right[2] - depth[2] * right[1],
    depth[2] * right[0] - depth[0] * right[2],
    depth[0] * right[1] - depth[1] * right[0],
  ];
  return { right, up, depth };
}

export interface Projected {
  x: number;
  y: number;
  depth: number;
}

/**
 * Wirft einen Punkt der Welt auf die Bildebene.
 *
 * Parallelprojektion, keine Perspektive: gleiche Laengen sollen gleich lang
 * erscheinen, egal wie der Roboter gedreht ist. Bei einer Perspektive waere
 * eine Drehung von einer Bewegung nach vorn nicht zu unterscheiden.
 *
 * `y` waechst nach unten, wie in SVG.
 */
export function project(p: Vec3, basis: ViewBasis): Projected {
  const punkt = (achse: Vec3) =>
    p[0] * achse[0] + p[1] * achse[1] + p[2] * achse[2];
  return { x: punkt(basis.right), y: -punkt(basis.up), depth: punkt(basis.depth) };
}

export interface Face {
  /** Ecken im Fahrzeugsystem, umlaufend. */
  points: Vec3[];
  /** Fuellfarbe. */
  fill: string;
  /** Kantenfarbe. */
  stroke: string;
  /** Fuer die Beschreibung fuer Vorleseprogramme. */
  name: string;
}

//: Halbe Abmessungen des Rumpfs, in willkuerlichen Einheiten. Das Verhaeltnis
//: entspricht dem Roboter: laenger als breit, flach.
const L = 0.5;
const B = 0.36;
const H = 0.14;

//: Raeder: Achse quer, also Kreise in der x-z-Ebene. Als Achtecke genaehert -
//: bei dieser Groesse ist der Unterschied zu einem Kreis nicht zu sehen, und
//: acht Ecken lassen sich wie jede andere Flaeche drehen und sortieren.
const RAD_RADIUS = 0.3;
const RAD_Y = B + 0.06;
const RAD_ECKEN = 8;

//: Die Achse sitzt am hinteren Ende, also in +x-Richtung. Nur laengs
//: verschoben: quer bleiben die Raeder, wo sie waren, denn dort sitzen sie am
//: Fahrzeug auch.
const RAD_X = 0.28;

function rad(y: number, name: string): Face {
  const points: Vec3[] = [];
  for (let i = 0; i < RAD_ECKEN; i += 1) {
    const winkel = (2 * Math.PI * i) / RAD_ECKEN;
    points.push([
      RAD_X + RAD_RADIUS * Math.cos(winkel),
      y,
      RAD_RADIUS * Math.sin(winkel),
    ]);
  }
  return { points, fill: "#3f3f46", stroke: "#18181b", name };
}

/**
 * Der Koerper, aus dem die Darstellung besteht.
 *
 * Die Vorderseite ist abgesetzt gefaerbt. Ohne sie waere an einem Quader nicht
 * zu erkennen, wohin der Roboter zeigt - und das ist die Frage, um die es bei
 * einer Lagedarstellung geht.
 *
 * **Vorn liegt bei -x.** Der Roboter faehrt in Richtung der negativen x-Achse
 * des Bausteins; die Achsen der Darstellung sind die des Bausteins, nicht die
 * der Fahrtrichtung. Deshalb zeigt der rote Pfeil der x-Achse nach hinten, und
 * das ist kein Fehler, sondern die Einbaulage.
 */
export const ROBOT_FACES: Face[] = [
  {
    name: "vorn",
    points: [
      [-L, -B, -H],
      [-L, B, -H],
      [-L, B, H],
      [-L, -B, H],
    ],
    fill: "#ef4444",
    stroke: "#7f1d1d",
  },
  {
    name: "hinten",
    points: [
      [L, -B, -H],
      [L, -B, H],
      [L, B, H],
      [L, B, -H],
    ],
    fill: "#a1a1aa",
    stroke: "#52525b",
  },
  {
    name: "links",
    points: [
      [-L, B, -H],
      [-L, B, H],
      [L, B, H],
      [L, B, -H],
    ],
    fill: "#d4d4d8",
    stroke: "#52525b",
  },
  {
    name: "rechts",
    points: [
      [-L, -B, -H],
      [L, -B, -H],
      [L, -B, H],
      [-L, -B, H],
    ],
    fill: "#d4d4d8",
    stroke: "#52525b",
  },
  {
    name: "oben",
    points: [
      [-L, -B, H],
      [L, -B, H],
      [L, B, H],
      [-L, B, H],
    ],
    fill: "#e4e4e7",
    stroke: "#52525b",
  },
  {
    name: "unten",
    points: [
      [-L, -B, -H],
      [-L, B, -H],
      [L, B, -H],
      [L, -B, -H],
    ],
    fill: "#71717a",
    stroke: "#3f3f46",
  },
  rad(RAD_Y, "Rad links"),
  rad(-RAD_Y, "Rad rechts"),
];

export interface Axis {
  from: Vec3;
  to: Vec3;
  colour: string;
  label: string;
}

//: Die Achsen des Fahrzeugs, mitgedreht. Sie machen sichtbar, welche Drehung
//: die Darstellung gerade zeigt, auch wenn der Koerper von hinten zu sehen ist.
export const ROBOT_AXES: Axis[] = [
  { from: [0, 0, 0], to: [0.9, 0, 0], colour: "#dc2626", label: "x" },
  { from: [0, 0, 0], to: [0, 0.9, 0], colour: "#16a34a", label: "y" },
  { from: [0, 0, 0], to: [0, 0, 0.9], colour: "#2563eb", label: "z" },
];

export interface ProjectedFace {
  name: string;
  fill: string;
  stroke: string;
  /** Ecken in Bildkoordinaten, als "x,y x,y ..." fuer SVG. */
  points: string;
  depth: number;
}

/**
 * Dreht und wirft alle Flaechen, von hinten nach vorn geordnet.
 *
 * Die Reihenfolge ist die ganze Verdeckungsrechnung: spaeter gezeichnete
 * Flaechen liegen oben. Sortiert wird nach der mittleren Tiefe. Das ist bei
 * einem Quader mit zwei Raedern richtig; bei zwei Flaechen, die sich
 * durchdringen, waere es das nicht - hier durchdringt sich nichts.
 */
export function projectedFaces(
  m: Matrix3,
  basis: ViewBasis = viewBasis(),
  faces: Face[] = ROBOT_FACES
): ProjectedFace[] {
  return faces
    .map((face) => {
      const ecken = face.points.map((punkt) =>
        project(rotate(m, punkt), basis)
      );
      const depth =
        ecken.reduce((summe, ecke) => summe + ecke.depth, 0) / ecken.length;
      return {
        name: face.name,
        fill: face.fill,
        stroke: face.stroke,
        points: ecken
          .map((ecke) => `${round(ecke.x)},${round(ecke.y)}`)
          .join(" "),
        depth,
      };
    })
    .sort((a, b) => a.depth - b.depth);
}

export interface ProjectedAxis {
  label: string;
  colour: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/** Dreht und wirft die Fahrzeugachsen. */
export function projectedAxes(
  m: Matrix3,
  basis: ViewBasis = viewBasis(),
  axes: Axis[] = ROBOT_AXES
): ProjectedAxis[] {
  return axes.map((axis) => {
    const von = project(rotate(m, axis.from), basis);
    const nach = project(rotate(m, axis.to), basis);
    return {
      label: axis.label,
      colour: axis.colour,
      x1: round(von.x),
      y1: round(von.y),
      x2: round(nach.x),
      y2: round(nach.y),
    };
  });
}

/**
 * Groesster Abstand vom Mittelpunkt, den die Form annehmen kann.
 *
 * Fuer den Maszstab: er muss von der Drehung unabhaengig sein, sonst wuechse
 * und schrumpfte der Roboter beim Drehen. Deshalb der Abstand der aeussersten
 * Ecke, nicht die Ausdehnung der gerade sichtbaren Ansicht.
 */
export function shapeRadius(faces: Face[] = ROBOT_FACES): number {
  let groesster = 0;
  for (const face of faces) {
    for (const [x, y, z] of face.points) {
      const abstand = Math.sqrt(x * x + y * y + z * z);
      if (abstand > groesster) {
        groesster = abstand;
      }
    }
  }
  return groesster;
}

function round(wert: number): number {
  return Math.round(wert * 1000) / 1000;
}
